import React, { useState } from 'react';
import { gql, useQuery, useLazyQuery } from '@apollo/client';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import moment from 'moment';
import { SlateDocument } from '@pv/common/components';
import { appHost } from '@pv/common/api';

const APP_HOST = appHost(process.env.REACT_APP_PV_ENV as string);

const PER_PAGE = 20;

const adminVenueAiReplySessionsListQuery = gql`
  query adminVenueAiReplySessionsListQuery($id: ID!, $page: Int!, $perPage: Int!) {
    adminVenue(id: $id) {
      id
      aiReplySessionsCount
      aiReplySessions(page: $page, perPage: $perPage) {
        id
        publicId
        createdAt
        event {
          id
          name
          startDate
          eventType
        }
        messageSuggestions {
          id
          approved
        }
        actionMessage {
          id
        }
      }
    }
  }
`;

const adminAiReplySessionDetailQuery = gql`
  query adminAiReplySessionDetailQuery($id: ID!) {
    adminAiReplySession(id: $id) {
      id
      publicId
      createdAt
      event {
        id
        name
        groupSize
        budget
        startDate
        eventType
      }
      triggerMessage {
        id
        plainTextFromHtml
        occurredAt
      }
      actionMessage {
        id
        draftMessageBody
        plainTextFromHtml
        plainTextWithoutSignature
        occurredAt
      }
      messageSuggestions {
        id
        content
        approved
        createdAt
        rephrasePrompt
      }
    }
  }
`;

// ─── Types ────────────────────────────────────────────────────────────────────

interface MessageSuggestion {
  id: string;
  content: string | null;
  approved: boolean;
  createdAt: string;
  rephrasePrompt: string | null;
}

interface Message {
  id: string;
  plainTextFromHtml?: string;
  plainTextWithoutSignature?: string;
  draftMessageBody?: unknown;
  occurredAt: string;
}

interface EventDetails {
  id: string;
  name: string | null;
  groupSize: number | null;
  budget: number | null;
  startDate: string | null;
  eventType: string | null;
}

interface AiReplySession {
  id: string;
  publicId: string | null;
  createdAt: string;
  event: EventDetails;
  triggerMessage: Message | null;
  actionMessage: Message | null;
  messageSuggestions: MessageSuggestion[];
}

interface AiReplySessionSummary {
  id: string;
  publicId: string | null;
  createdAt: string;
  event: Pick<EventDetails, 'id' | 'name' | 'startDate' | 'eventType'>;
  messageSuggestions: Pick<MessageSuggestion, 'id' | 'approved'>[];
  actionMessage: { id: string } | null;
}

// ─── Diff utilities ───────────────────────────────────────────────────────────

type DiffPart = { value: string; added?: boolean; removed?: boolean };

function wordDiff(oldStr: string, newStr: string): DiffPart[] {
  const tokenize = (s: string) => s.match(/\S+|\s+/g) ?? [];
  const oldTokens = tokenize(oldStr);
  const newTokens = tokenize(newStr);
  const m = oldTokens.length;
  const n = newTokens.length;

  const dp: number[][] = Array.from({ length: m + 1 }, () =>
    new Array(n + 1).fill(0)
  );
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] =
        oldTokens[i - 1] === newTokens[j - 1]
          ? dp[i - 1][j - 1] + 1
          : Math.max(dp[i - 1][j], dp[i][j - 1]);
    }
  }

  const parts: DiffPart[] = [];
  let i = m;
  let j = n;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && oldTokens[i - 1] === newTokens[j - 1]) {
      parts.unshift({ value: oldTokens[i - 1] });
      i--;
      j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      parts.unshift({ value: newTokens[j - 1], added: true });
      j--;
    } else {
      parts.unshift({ value: oldTokens[i - 1], removed: true });
      i--;
    }
  }
  return parts;
}

function similarityScore(oldStr: string, newStr: string): number {
  if (!oldStr && !newStr) return 100;
  if (!oldStr || !newStr) return 0;
  const parts = wordDiff(oldStr, newStr);
  const matched = parts
    .filter((p) => !p.added && !p.removed)
    .reduce((acc, p) => acc + p.value.length, 0);
  const total = Math.max(oldStr.length, newStr.length);
  return Math.round((matched / total) * 100);
}

function stripHtml(html: string): string {
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent ?? div.innerText ?? '';
}

// ─── Small shared components ──────────────────────────────────────────────────

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="overline" color="textSecondary" display="block" gutterBottom>
    {children}
  </Typography>
);

const EventMeta = ({ event }: { event: EventDetails }) => {
  const details: string[] = [];
  if (event.startDate) details.push(moment(event.startDate).format('MMM D, YYYY'));
  if (event.eventType) details.push(event.eventType);
  if (event.groupSize) details.push(`${event.groupSize} guests`);
  if (event.budget) details.push(`$${(event.budget / 100).toLocaleString()} budget`);
  return (
    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mt={0.5}>
      {details.map((d) => (
        <Chip key={d} label={d} size="small" variant="outlined" />
      ))}
      <Button
        size="small"
        variant="text"
        href={`${APP_HOST}/events/${event.id}`}
        target="_blank"
        rel="noreferrer"
        sx={{ ml: 0.5 }}
      >
        View event →
      </Button>
    </Box>
  );
};

// ─── Timeline tab ─────────────────────────────────────────────────────────────

const DOT_SIZE = 10;
const LINE_WIDTH = 2;

const TimelineDot = ({ color }: { color: string }) => (
  <Box
    sx={{
      width: DOT_SIZE,
      height: DOT_SIZE,
      borderRadius: '50%',
      backgroundColor: color,
      flexShrink: 0,
      mt: '5px',
    }}
  />
);

const TimelineItem = ({
  color,
  label,
  timestamp,
  chip,
  isLast,
  children,
}: {
  color: string;
  label: string;
  timestamp?: string;
  chip?: React.ReactNode;
  isLast?: boolean;
  children: React.ReactNode;
}) => (
  <Box display="flex" gap={1.5}>
    <Box display="flex" flexDirection="column" alignItems="center" flexShrink={0} width={DOT_SIZE}>
      <TimelineDot color={color} />
      {!isLast && (
        <Box sx={{ width: LINE_WIDTH, flexGrow: 1, backgroundColor: 'grey.300', mt: '2px' }} />
      )}
    </Box>
    <Box pb={isLast ? 0 : 2} flex={1} minWidth={0}>
      <Box display="flex" alignItems="center" gap={1} mb={0.5} flexWrap="wrap">
        <Typography variant="overline" color="textSecondary" lineHeight={1.4}>
          {label}
        </Typography>
        {chip}
        {timestamp && (
          <Typography variant="caption" color="textSecondary" ml="auto">
            {moment(timestamp).format('MMM D, h:mm a')}
          </Typography>
        )}
      </Box>
      {children}
    </Box>
  </Box>
);

const TextBlock = ({ children }: { children: React.ReactNode }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        backgroundColor: theme.palette.grey[50],
        border: `1px solid ${theme.palette.grey[200]}`,
        borderRadius: 1,
        p: 1.5,
        fontSize: '0.875rem',
        wordBreak: 'break-word',
        '& p': { margin: 0 },
        '& .MuiTypography-root': { fontSize: 'inherit' },
      }}
    >
      {children}
    </Box>
  );
};

const TimelineTab = ({ session }: { session: AiReplySession }) => {
  const steps: React.ReactNode[] = [];
  const total =
    (session.triggerMessage ? 1 : 0) +
    session.messageSuggestions.length +
    (session.actionMessage ? 1 : 0);
  let stepIndex = 0;

  if (session.triggerMessage) {
    const isLast = ++stepIndex === total;
    steps.push(
      <TimelineItem
        key="trigger"
        color="#94a3b8"
        label="Guest message"
        timestamp={session.triggerMessage.occurredAt}
        isLast={isLast}
      >
        <TextBlock>{session.triggerMessage.plainTextFromHtml || <em>empty</em>}</TextBlock>
      </TimelineItem>
    );
  }

  session.messageSuggestions.forEach((s, i) => {
    const isLast = ++stepIndex === total;
    const label = i === 0 ? 'Initial AI suggestion' : `Rephrase ${i}`;
    steps.push(
      <TimelineItem
        key={s.id}
        color={s.approved ? '#22c55e' : '#818cf8'}
        label={label}
        timestamp={s.createdAt}
        chip={s.approved ? <Chip label="accepted" size="small" color="success" /> : undefined}
        isLast={isLast}
      >
        {!!(i > 0 && s.rephrasePrompt) && (
          <Box
            sx={{
              mb: 1,
              pl: 1.5,
              borderLeft: '3px solid',
              borderColor: 'warning.light',
              color: 'text.secondary',
              fontSize: '0.8125rem',
              fontStyle: 'italic',
            }}
          >
            "{s.rephrasePrompt}"
          </Box>
        )}
        <TextBlock>
          <div dangerouslySetInnerHTML={{ __html: s.content || '<em>empty</em>' }} />
        </TextBlock>
      </TimelineItem>
    );
  });

  if (session.actionMessage) {
    steps.push(
      <TimelineItem
        key="sent"
        color="#f59e0b"
        label="Final message sent"
        timestamp={session.actionMessage.occurredAt}
        isLast
      >
        <TextBlock>
          {session.actionMessage.draftMessageBody ? (
            <SlateDocument source={session.actionMessage.draftMessageBody} />
          ) : (
            session.actionMessage.plainTextFromHtml || <em>empty</em>
          )}
        </TextBlock>
      </TimelineItem>
    );
  }

  return <Box>{steps}</Box>;
};

// ─── Diff tab ─────────────────────────────────────────────────────────────────

const InlineDiff = ({ parts }: { parts: DiffPart[] }) => (
  <span style={{ fontSize: '0.875rem', lineHeight: 1.6, wordBreak: 'break-word' }}>
    {parts.map((part, i) => {
      if (part.removed) {
        return (
          <mark
            key={i}
            style={{ backgroundColor: '#fecaca', textDecoration: 'line-through', borderRadius: 2 }}
          >
            {part.value}
          </mark>
        );
      }
      if (part.added) {
        return (
          <mark key={i} style={{ backgroundColor: '#bbf7d0', borderRadius: 2 }}>
            {part.value}
          </mark>
        );
      }
      return <span key={i}>{part.value}</span>;
    })}
  </span>
);

const DiffTab = ({
  session,
  acceptedSuggestion,
}: {
  session: AiReplySession;
  acceptedSuggestion: MessageSuggestion;
}) => {
  const theme = useTheme();

  const suggestionText = stripHtml(acceptedSuggestion.content ?? '').trim();
  const sentText = (
    session.actionMessage?.plainTextWithoutSignature ??
    session.actionMessage?.plainTextFromHtml ??
    ''
  ).trim();

  const score = similarityScore(suggestionText, sentText);
  const userEdited = suggestionText !== sentText;

  const TOO_LONG = 3000;
  const canDiff = suggestionText.length + sentText.length < TOO_LONG;
  const diffParts = canDiff ? wordDiff(suggestionText, sentText) : null;

  const colSx = {
    flex: 1,
    minWidth: 0,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 1,
    overflow: 'hidden',
  };

  const headerSx = (bg: string) => ({
    px: 1.5,
    py: 0.75,
    backgroundColor: bg,
    borderBottom: `1px solid ${theme.palette.divider}`,
  });

  const bodySx = {
    p: 1.5,
    fontSize: '0.875rem',
    wordBreak: 'break-word',
    '& p': { marginTop: 0, marginBottom: '0.5em' },
    '& .MuiTypography-root': { fontSize: 'inherit' },
  };

  return (
    <Box>
      <Box display="flex" gap={1} mb={2} flexWrap="wrap" alignItems="center">
        <Tooltip title="Character-level similarity between accepted suggestion and final sent message">
          <Chip
            label={`${score}% similar`}
            size="small"
            color={score >= 90 ? 'success' : score >= 60 ? 'warning' : 'error'}
          />
        </Tooltip>
        <Chip
          label={userEdited ? 'User edited after accept' : 'Sent as-is'}
          size="small"
          variant="outlined"
          color={userEdited ? 'warning' : 'default'}
        />
      </Box>

      {diffParts ? (
        <Box>
          <SectionLabel>Word diff — accepted suggestion → final sent</SectionLabel>
          <Box
            sx={{
              ...bodySx,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              p: 1.5,
            }}
          >
            <InlineDiff parts={diffParts} />
          </Box>
          <Box display="flex" gap={1} mt={1}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.75rem' }}>
              <mark style={{ backgroundColor: '#fecaca', borderRadius: 2, padding: '0 3px' }}>removed</mark>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.75rem' }}>
              <mark style={{ backgroundColor: '#bbf7d0', borderRadius: 2, padding: '0 3px' }}>added</mark>
            </Box>
          </Box>
        </Box>
      ) : (
        <Box display="flex" gap={2}>
          <Box sx={colSx}>
            <Box sx={headerSx('#fff5f5')}>
              <Typography variant="caption" fontWeight="bold">Accepted suggestion</Typography>
            </Box>
            <Box
              sx={bodySx}
              dangerouslySetInnerHTML={{ __html: acceptedSuggestion.content ?? '' }}
            />
          </Box>
          <Box sx={colSx}>
            <Box sx={headerSx('#f0fff4')}>
              <Typography variant="caption" fontWeight="bold">Final message sent</Typography>
            </Box>
            <Box sx={bodySx}>
              {session.actionMessage?.draftMessageBody ? (
                <SlateDocument source={session.actionMessage.draftMessageBody} />
              ) : (
                <span>{sentText}</span>
              )}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

// ─── Rephrase table tab ───────────────────────────────────────────────────────

const TableTab = ({ session }: { session: AiReplySession }) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const theme = useTheme();

  const rows = session.messageSuggestions.map((s, i) => ({
    id: s.id,
    step: i === 0 ? 'Initial' : `Rephrase ${i}`,
    prompt: i === 0 ? '—' : s.rephrasePrompt ?? '—',
    content: s.content,
    accepted: s.approved,
    timestamp: s.createdAt,
  }));

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontWeight: 'bold' }}>Step</TableCell>
          <TableCell sx={{ fontWeight: 'bold' }}>User prompt</TableCell>
          <TableCell sx={{ fontWeight: 'bold' }}>Time</TableCell>
          <TableCell sx={{ fontWeight: 'bold' }}>Accepted?</TableCell>
          <TableCell />
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <React.Fragment key={row.id}>
            <TableRow
              hover
              sx={{ cursor: 'pointer' }}
              onClick={() => setExpandedId(expandedId === row.id ? null : row.id)}
            >
              <TableCell>{row.step}</TableCell>
              <TableCell
                sx={{
                  maxWidth: 200,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontStyle: row.prompt === '—' ? 'normal' : 'italic',
                  color: row.prompt === '—' ? 'text.disabled' : 'text.primary',
                }}
              >
                {row.prompt}
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary', fontSize: '0.75rem' }}>
                {moment(row.timestamp).format('h:mm a')}
              </TableCell>
              <TableCell>
                {row.accepted && <Chip label="yes" size="small" color="success" />}
              </TableCell>
              <TableCell padding="none" align="right">
                <IconButton size="small">
                  {expandedId === row.id ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </IconButton>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell colSpan={5} sx={{ p: 0, borderBottom: expandedId === row.id ? undefined : 'none' }}>
                <Collapse in={expandedId === row.id} unmountOnExit>
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: theme.palette.grey[50],
                      fontSize: '0.875rem',
                      wordBreak: 'break-word',
                      '& p': { margin: 0 },
                    }}
                    dangerouslySetInnerHTML={{ __html: row.content ?? '<em>empty</em>' }}
                  />
                </Collapse>
              </TableCell>
            </TableRow>
          </React.Fragment>
        ))}
      </TableBody>
    </Table>
  );
};

// ─── Session card (full detail) ───────────────────────────────────────────────

const SessionCard = ({ session }: { session: AiReplySession }) => {
  const [tab, setTab] = useState<string>('timeline');

  const acceptedSuggestion =
    session.messageSuggestions.find((s) => s.approved) ??
    session.messageSuggestions[session.messageSuggestions.length - 1] ??
    null;

  const hasDiff = !!(session.actionMessage && acceptedSuggestion);
  const hasAnySuggestions = session.messageSuggestions.length > 0;

  return (
    <Box>
      <Box mb={1}>
        <EventMeta event={session.event} />
      </Box>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} textColor="primary" indicatorColor="primary">
          <Tab label="Timeline" value="timeline" />
          <Tab label="Diff" value="diff" />
          {hasAnySuggestions && <Tab label="Message Suggestions" value="suggestions" />}
        </Tabs>
      </Box>
      {tab === 'timeline' && <TimelineTab session={session} />}
      {tab === 'diff' && (
        hasDiff && acceptedSuggestion ? (
          <DiffTab session={session} acceptedSuggestion={acceptedSuggestion} />
        ) : (
          <Typography variant="body2" color="textSecondary">
            No message has been sent for this session yet.
          </Typography>
        )
      )}
      {tab === 'suggestions' && session.messageSuggestions.length > 0 && (
        <TableTab session={session} />
      )}
    </Box>
  );
};

// ─── Expandable summary row ───────────────────────────────────────────────────

const SessionRow = ({ summary }: { summary: AiReplySessionSummary }) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [loadDetail, { data, loading }] = useLazyQuery(adminAiReplySessionDetailQuery);

  const handleToggle = () => {
    if (!open && !data) {
      loadDetail({ variables: { id: summary.id } });
    }
    setOpen((v) => !v);
  };

  const accepted = summary.messageSuggestions.some((s) => s.approved);
  const rephraseCount = Math.max(0, summary.messageSuggestions.length - 1);
  const session: AiReplySession | null = data?.adminAiReplySession ?? null;

  return (
    <>
      <TableRow
        hover
        sx={{ cursor: 'pointer', '& td': { verticalAlign: 'middle' } }}
        onClick={handleToggle}
      >
        <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary', fontSize: '0.8rem' }}>
          {moment(summary.createdAt).format('MMM D, YYYY h:mm a')}
        </TableCell>
        <TableCell>
          <Typography variant="body2" fontWeight={500}>
            {summary.event.name || `Event #${summary.event.id}`}
          </Typography>
          {summary.event.startDate && (
            <Typography variant="caption" color="textSecondary">
              {summary.event.eventType ? `${summary.event.eventType} · ` : ''}
              {moment(summary.event.startDate).format('MMM D, YYYY')}
            </Typography>
          )}
        </TableCell>
        <TableCell align="center">
          {accepted ? (
            <Chip label="accepted" size="small" color="success" />
          ) : (
            <Typography variant="caption" color="textSecondary">—</Typography>
          )}
        </TableCell>
        <TableCell align="center">
          {rephraseCount > 0 ? (
            <Chip label={rephraseCount} size="small" variant="outlined" />
          ) : (
            <Typography variant="caption" color="textSecondary">0</Typography>
          )}
        </TableCell>
        <TableCell align="center">
          {summary.actionMessage ? (
            <Chip label="sent" size="small" color="primary" variant="outlined" />
          ) : (
            <Typography variant="caption" color="textSecondary">—</Typography>
          )}
        </TableCell>
        <TableCell padding="none" align="right">
          <IconButton size="small">
            {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={6} sx={{ p: 0, borderBottom: open ? undefined : 'none' }}>
          <Collapse in={open} unmountOnExit>
            <Box
              sx={{
                p: 2,
                backgroundColor: theme.palette.grey[50],
                borderTop: `1px solid ${theme.palette.divider}`,
              }}
            >
              {loading && <Typography color="textSecondary" variant="body2">Loading…</Typography>}
              {session && <SessionCard session={session} />}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

// ─── Panel ────────────────────────────────────────────────────────────────────

export const AiReplySessionsPanel = ({ venueId }: { venueId: string }) => {
  const theme = useTheme();
  const [page, setPage] = useState(1);

  const { data, loading } = useQuery(adminVenueAiReplySessionsListQuery, {
    variables: { id: venueId, page, perPage: PER_PAGE },
  });

  const sessions: AiReplySessionSummary[] = data?.adminVenue?.aiReplySessions ?? [];
  const totalCount: number = data?.adminVenue?.aiReplySessionsCount ?? 0;
  const totalPages = Math.ceil(totalCount / PER_PAGE);

  return (
    <Box p={theme.spacing(2)}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h6">
          AI Reply Sessions
          {totalCount > 0 && (
            <Typography component="span" variant="body2" color="textSecondary" ml={1}>
              ({totalCount} total)
            </Typography>
          )}
        </Typography>
      </Stack>

      {loading && <Typography color="textSecondary">Loading…</Typography>}

      {!loading && sessions.length === 0 && (
        <Typography color="textSecondary">No AI reply sessions found for this venue.</Typography>
      )}

      {sessions.length > 0 && (
        <>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Event</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="center">Accepted</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="center">Rephrases</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="center">Sent</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {sessions.map((summary) => (
                <SessionRow key={summary.id} summary={summary} />
              ))}
            </TableBody>
          </Table>

          {totalPages > 1 && (
            <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1} mt={2}>
              <Typography variant="caption" color="textSecondary">
                Page {page} of {totalPages}
              </Typography>
              <IconButton size="small" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" disabled={page === totalPages} onClick={() => setPage((p) => p + 1)}>
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </Stack>
          )}
        </>
      )}
    </Box>
  );
};
